"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  ResultCard,
  ScriptTextArea,
} from "@/components/tools/transliteration-more/shared";
import { t } from "@/lib/i18n/interpolate";
import {
  type KanaScript,
  kanaChartRows,
  kanaToRomaji,
  type LongO,
  type LongVowels,
  type RomajiOptions,
  romajiToKana,
  type Spelling,
  toHiragana,
  toKatakana,
} from "@/lib/tool-kit/transliteration-more/romaji";

/** English strings. A translated content file overrides these through `ui`. */
export const ROMAJI_CONVERTER_STRINGS = {
  mode: "What to convert",
  modes: {
    "to-romaji": "Kana → Romaji",
    "to-kana": "Romaji → Kana",
    "kana-kana": "Hiragana ↔ Katakana",
  },
  kanaLabel: "Japanese kana",
  kanaHint:
    "Hiragana or katakana, with spaces between words. Kanji cannot be read and are left as they are.",
  romajiLabel: "Romaji",
  romajiHint:
    "Hepburn romaji. Macrons (ā ī ū ē ō), n' before a vowel, and doubled consonants are understood.",
  kanaKanaLabel: "Hiragana or katakana",
  samples: {
    kana: "わたし は とうきょう へ いきました。",
    katakana: "コンピュータ ニューヨーク ケンタッキー",
    romaji: "tōkyō konpyūta shin'yō kippu",
    both: "ひらがな カタカナ ヴァイオリン",
  },
  sampleHiragana: "Hiragana sample",
  sampleKatakana: "Katakana sample",
  sampleInsert: "Insert a sample",
  optionsLegend: "Options",
  longVowelsLabel: "Long vowels",
  longVowelsOptions: {
    macron: "Macrons — tōkyō (revised Hepburn)",
    plain: "No marks — tokyo (signs, passports)",
    kana: "As spelled — toukyou, konpyuuta",
  },
  particles: "Read a lone は as wa and a lone へ as e (particles)",
  capitalize: "Capitalize each word",
  scriptLabel: "Write the kana as",
  scriptOptions: { hiragana: "Hiragana", katakana: "Katakana" },
  spellingLabel: "Spelling to read",
  spellingOptions: {
    hepburn: "Hepburn — ti is ティ, tu is トゥ",
    kunrei: "Kunrei-shiki / Nihon-shiki — ti is ち, tu is つ",
  },
  longOLabel: "Read ō as",
  longOOptions: { ou: "おう (the common spelling)", oo: "おお" },
  romajiHeading: "Romaji",
  kanaHeading: "Kana",
  hiraganaHeading: "Hiragana",
  katakanaHeading: "Katakana",
  hepburnUse:
    "Revised (modified) Hepburn, as in the Library of Congress table: ん is always n, を is o, and n' separates ん from a vowel or y.",
  kanaUse: "Written from the romaji above.",
  enterText: "Type or paste some text to convert.",
  kanjiNotice:
    "one:{count} kanji could not be read and was left unchanged.|other:{count} kanji could not be read and were left unchanged. Type the kana reading to romanize them.",
  unknownChars: "Left unchanged because they are not romaji: {chars}.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Kana chart",
  hiraganaColumn: "Hiragana",
  katakanaColumn: "Katakana",
  romajiColumn: "Hepburn",
  groups: {
    basic: "Basic kana",
    voiced: "Voiced kana",
    yoon: "Combinations with small ゃ ゅ ょ",
    extended: "Extended kana (mostly foreign words)",
  },
  tableNote:
    "ゐ, ゑ and を are i, e and o in the revised system. A small っ doubles the next consonant (tch before ち), and ー lengthens the vowel before it.",
} as const;

type Strings = typeof ROMAJI_CONVERTER_STRINGS;
type Mode = "to-romaji" | "to-kana" | "kana-kana";

const DEFAULT_OPTIONS: RomajiOptions = {
  longVowels: "macron",
  particles: true,
  capitalize: false,
  script: "hiragana",
  spelling: "hepburn",
  longO: "ou",
};

const GROUPS = ["basic", "voiced", "yoon", "extended"] as const;

function KanaChart({ strings }: { strings: Strings }) {
  const rows = kanaChartRows();
  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      {GROUPS.map((group) => (
        <div className="space-y-2" key={group}>
          <p className={LABEL}>{strings.groups[group]}</p>
          <DataTable
            head={[
              strings.hiraganaColumn,
              strings.katakanaColumn,
              strings.romajiColumn,
            ]}
            rows={rows
              .filter((row) => row.group === group)
              .map((row) => [row.hiragana, row.katakana, row.romaji])}
          />
        </div>
      ))}
      <p className={HINT}>{strings.tableNote}</p>
    </section>
  );
}

function ToRomajiOptions({
  options,
  setOptions,
  strings,
}: {
  options: RomajiOptions;
  setOptions: (update: (o: RomajiOptions) => RomajiOptions) => void;
  strings: Strings;
}) {
  return (
    <>
      <div className="max-w-md">
        <SelectField<LongVowels>
          id="rj-long"
          label={strings.longVowelsLabel}
          onChange={(longVowels) => setOptions((o) => ({ ...o, longVowels }))}
          options={[
            { value: "macron", label: strings.longVowelsOptions.macron },
            { value: "plain", label: strings.longVowelsOptions.plain },
            { value: "kana", label: strings.longVowelsOptions.kana },
          ]}
          value={options.longVowels}
        />
      </div>
      <Checkbox
        checked={options.particles}
        label={strings.particles}
        onChange={(particles) => setOptions((o) => ({ ...o, particles }))}
      />
      <Checkbox
        checked={options.capitalize}
        label={strings.capitalize}
        onChange={(capitalize) => setOptions((o) => ({ ...o, capitalize }))}
      />
    </>
  );
}

function ToKanaOptions({
  options,
  setOptions,
  strings,
}: {
  options: RomajiOptions;
  setOptions: (update: (o: RomajiOptions) => RomajiOptions) => void;
  strings: Strings;
}) {
  return (
    <div className="grid max-w-3xl gap-3 sm:grid-cols-3">
      <SelectField<KanaScript>
        id="rj-script"
        label={strings.scriptLabel}
        onChange={(script) => setOptions((o) => ({ ...o, script }))}
        options={[
          { value: "hiragana", label: strings.scriptOptions.hiragana },
          { value: "katakana", label: strings.scriptOptions.katakana },
        ]}
        value={options.script}
      />
      <SelectField<Spelling>
        id="rj-spelling"
        label={strings.spellingLabel}
        onChange={(spelling) => setOptions((o) => ({ ...o, spelling }))}
        options={[
          { value: "hepburn", label: strings.spellingOptions.hepburn },
          { value: "kunrei", label: strings.spellingOptions.kunrei },
        ]}
        value={options.spelling}
      />
      <SelectField<LongO>
        id="rj-long-o"
        label={strings.longOLabel}
        onChange={(longO) => setOptions((o) => ({ ...o, longO }))}
        options={[
          { value: "ou", label: strings.longOOptions.ou },
          { value: "oo", label: strings.longOOptions.oo },
        ]}
        value={options.longO}
      />
    </div>
  );
}

function Results({
  mode,
  text,
  options,
  strings,
}: {
  mode: Mode;
  text: string;
  options: RomajiOptions;
  strings: Strings;
}) {
  if (mode === "kana-kana") {
    return (
      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.kanaHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <ResultCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            lang="ja"
            text={toHiragana(text)}
            title={strings.hiraganaHeading}
          />
          <ResultCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            lang="ja"
            text={toKatakana(text)}
            title={strings.katakanaHeading}
          />
        </div>
      </section>
    );
  }
  const forward = mode === "to-romaji";
  const result = forward
    ? kanaToRomaji(text, options)
    : romajiToKana(text, options);
  const plainUnknown = forward ? [] : result.unknown;
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>
        {forward ? strings.romajiHeading : strings.kanaHeading}
      </h2>
      {result.kanji > 0 ? (
        <Notice>{t(strings.kanjiNotice, { count: result.kanji })}</Notice>
      ) : null}
      {plainUnknown.length > 0 ? (
        <Notice>
          {t(strings.unknownChars, { chars: plainUnknown.join(" ") })}
        </Notice>
      ) : null}
      <ResultCard
        copiedLabel={strings.copied}
        copyLabel={strings.copy}
        lang={forward ? "ja-Latn-hepburn" : "ja"}
        text={result.text}
        title={forward ? strings.romajiHeading : strings.kanaHeading}
        use={forward ? strings.hepburnUse : strings.kanaUse}
      />
    </section>
  );
}

export default function RomajiConverterWidget() {
  const strings = useToolUi(ROMAJI_CONVERTER_STRINGS);
  const [mode, setMode] = useState<Mode>("to-romaji");
  const [kana, setKana] = useState<string>(strings.samples.kana);
  const [romaji, setRomaji] = useState<string>(strings.samples.romaji);
  const [both, setBoth] = useState<string>(strings.samples.both);
  const [options, setOptions] = useState<RomajiOptions>(DEFAULT_OPTIONS);
  const values: Record<Mode, string> = {
    "to-romaji": kana,
    "to-kana": romaji,
    "kana-kana": both,
  };
  const setters: Record<Mode, (value: string) => void> = {
    "to-romaji": setKana,
    "to-kana": setRomaji,
    "kana-kana": setBoth,
  };
  const labels: Record<Mode, string> = {
    "to-romaji": strings.kanaLabel,
    "to-kana": strings.romajiLabel,
    "kana-kana": strings.kanaKanaLabel,
  };
  const hints: Record<Mode, string | undefined> = {
    "to-romaji": strings.kanaHint,
    "to-kana": strings.romajiHint,
    "kana-kana": undefined,
  };
  const current = values[mode];

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.mode}
        name="rj-mode"
        onChange={setMode}
        options={(["to-romaji", "to-kana", "kana-kana"] as const).map(
          (value) => ({ value, label: strings.modes[value] })
        )}
        value={mode}
      />
      <ScriptTextArea
        dir={mode === "to-kana" ? "ltr" : "auto"}
        hint={hints[mode]}
        id="rj-input"
        label={labels[mode]}
        lang={mode === "to-kana" ? "ja-Latn" : "ja"}
        onChange={setters[mode]}
        value={current}
      />
      <div className="flex flex-wrap gap-2">
        {mode === "to-romaji" ? (
          <>
            <button
              className={BUTTON_SMALL}
              onClick={() => setKana(strings.samples.kana)}
              type="button"
            >
              {strings.sampleHiragana}
            </button>
            <button
              className={BUTTON_SMALL}
              onClick={() => setKana(strings.samples.katakana)}
              type="button"
            >
              {strings.sampleKatakana}
            </button>
          </>
        ) : (
          <button
            className={BUTTON_SMALL}
            onClick={() =>
              setters[mode](
                mode === "to-kana"
                  ? strings.samples.romaji
                  : strings.samples.both
              )
            }
            type="button"
          >
            {strings.sampleInsert}
          </button>
        )}
      </div>
      {mode === "kana-kana" ? null : (
        <fieldset className="space-y-3">
          <legend className={LABEL}>{strings.optionsLegend}</legend>
          {mode === "to-romaji" ? (
            <ToRomajiOptions
              options={options}
              setOptions={setOptions}
              strings={strings}
            />
          ) : (
            <ToKanaOptions
              options={options}
              setOptions={setOptions}
              strings={strings}
            />
          )}
        </fieldset>
      )}
      {current.trim() === "" ? (
        <Notice>{strings.enterText}</Notice>
      ) : (
        <Results
          mode={mode}
          options={options}
          strings={strings}
          text={current}
        />
      )}
      <KanaChart strings={strings} />
    </div>
  );
}
